// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useEffect, useRef } from 'react';
import { useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import { isGroupInteractive, isInteractive } from '../option/utils/filter-options';
import { useHighlightedOption, } from '../options-list/utils/use-highlight-option';
import { getOptionId } from '../options-list/utils/use-ids';
import { useMenuKeyboard } from '../options-list/utils/use-keyboard';
import { useAnnouncement } from './use-announcement';
import { connectOptionsByValue } from './utils/connect-options';
import { getListboxNavigation } from './utils/navigation';
import { getListboxOptionProps, getListMouseHandlers, getSelectableOption, isAnnouncedSelected, isHighlightable, } from './utils/option-props';
/**
 * List state shared by all list components: highlight, keyboard navigation, the choose guard, mouse handlers,
 * row props, scrolling the highlighted row into view, the active descendant and the live region announcement.
 * Focus, open state, filter, type-ahead and the highlight on open stay with the callers, because they differ
 * per component.
 */
export function useListbox({ options, selectedOptions: selectedOptionsProp, selection, interactiveGroups, onSelect, spaceSelects, onEscape, getParent, selectedAriaLabel, renderHighlightedAriaLive, }) {
    const listId = useUniqueId('option-list');
    const selectedOptions = connectOptionsByValue(options, selectedOptionsProp);
    const selectedSet = new Set(selectedOptions);
    const [{ highlightType, highlightedOption, highlightedIndex }, highlight] = useHighlightedOption({
        options,
        isHighlightable: (option) => isHighlightable(option, interactiveGroups),
    });
    const interactivityCheck = interactiveGroups ? isGroupInteractive : isInteractive;
    const selectOption = (option) => {
        const optionToSelect = getSelectableOption(option, highlightedOption, interactivityCheck);
        if (optionToSelect) {
            onSelect(optionToSelect);
        }
    };
    const onKeyDown = useMenuKeyboard({
        ...getListboxNavigation({
            options,
            highlightedIndex,
            highlightedOption,
            interactiveGroups,
            moveHighlight: highlight.moveHighlightWithKeyboard,
            goHome: highlight.goHomeWithKeyboard,
            goEnd: highlight.goEndWithKeyboard,
        }),
        selectOption,
        goHome: highlight.goHomeWithKeyboard,
        goEnd: highlight.goEndWithKeyboard,
        closeDropdown: onEscape,
        // With 'keyboard-highlight', Space chooses only while an option has a keyboard highlight, and types otherwise.
        preventNativeSpace: spaceSelects === 'always' || (!!highlightedOption && highlightType.type === 'keyboard'),
    });
    const mouseHandlers = getListMouseHandlers({
        options,
        selectOption,
        setHighlightedIndexWithMouse: highlight.setHighlightedIndexWithMouse,
    });
    const getOptionProps = (option, index) => getListboxOptionProps(option, index, {
        options,
        selectedSet,
        selection,
        highlightedOption,
        interactiveGroups,
        listId,
    });
    const announceSelected = isAnnouncedSelected(highlightedOption, selectedSet, selection);
    const announcement = useAnnouncement({
        announceSelected,
        highlightedOption,
        getParent,
        selectedAriaLabel,
        renderHighlightedAriaLive,
    });
    const activeDescendantId = getOptionId(listId, highlightedIndex);
    // Keeps the highlighted option scrolled into view in the list passed `ref={scrollToIndex}`.
    const scrollToIndex = useRef(null);
    useEffect(() => {
        var _a;
        (_a = scrollToIndex.current) === null || _a === void 0 ? void 0 : _a.call(scrollToIndex, highlightedIndex);
    }, [highlightedIndex]);
    const listRef = useRef(null);
    const getListProps = ({ withKeyboard }) => {
        const listProps = { id: listId, ref: listRef, ...mouseHandlers };
        if (withKeyboard) {
            listProps.onKeyDown = onKeyDown;
            listProps.nativeAttributes = { 'aria-activedescendant': activeDescendantId };
        }
        return listProps;
    };
    return {
        listId,
        highlightedIndex,
        highlightType,
        highlightedOption,
        activeDescendantId,
        scrollToIndex,
        selectedOptions,
        highlight,
        selectOption,
        onKeyDown,
        mouseHandlers,
        listRef,
        getListProps,
        getOptionProps,
        announceSelected,
        announcement,
    };
}
//# sourceMappingURL=use-listbox.js.map